import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Sparkles, ShieldCheck, Clock, ArrowRight } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/useSeo";

const TRIAL_CATEGORIES = [
  "Images & Visual Content",
  "Marketing & Business Promotion",
  "Admin & Office Productivity",
];

export default function StartTrialPage() {
  const { user, setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("idle"); // idle | starting | success | error
  const [err, setErr] = useState("");
  useSeo({ title: "Start your 24-hour free pass", noindex: true });

  // Route-guard: if not logged in, send to register; if not verified, send to verify.
  useEffect(() => {
    if (user === false) { navigate("/register?plan=trial", { replace: true }); return; }
    if (!user) return;
    if (!user.email_verified) { navigate("/verify-email?next=/start-trial", { replace: true }); return; }
    // Once we've entered the flow, do not redirect on membership change — let the success screen paint.
    if (state !== "idle") return;
    if (user.membership === "trial") { navigate("/app", { replace: true }); return; }
    if (["student", "pro", "lifetime"].includes(user.membership)) { navigate("/app", { replace: true }); return; }
    if (user.trial_used) { navigate("/pricing?trial=used", { replace: true }); return; }
    // Eligible — auto-start.
    setState("starting");
    api.post("/trial/start").then(async ({ data }) => {
      if (data.user) setUser(data.user);
      else if (refresh) await refresh();
      setState("success");
      toast.success("Your 24-hour pass is active");
    }).catch((e) => {
      setErr(apiError(e));
      setState("error");
    });
  }, [user, navigate, setUser, refresh, state]);

  if (!user) return <div className="p-16 text-center text-slate-400" data-testid="trial-loading">Loading…</div>;

  const start = async () => {
    setState("starting"); setErr("");
    try {
      const { data } = await api.post("/trial/start");
      if (data.user) setUser(data.user);
      else if (refresh) await refresh();
      setState("success");
      toast.success("Your 24-hour pass is active");
    } catch (e) {
      setErr(apiError(e));
      setState("error");
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-[#FAFAF7]" data-testid="start-trial-page">
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5" /> Free — No card needed
        </div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-4 text-[#0B0F0D]">
          {state === "success" ? "You're in!" : "Starting your 24-hour pass"}
        </h1>
        <p className="text-slate-600 mt-3 leading-relaxed">
          Hi <span className="font-semibold">{user.name?.split(" ")[0] || "there"}</span> — you'll get full Pro-level access
          to three sample categories for the next 24 hours. Everything else stays locked until you pick a plan.
        </p>

        <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-200">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Included categories</p>
          <ul className="mt-3 space-y-2">
            {TRIAL_CATEGORIES.map((c) => (
              <li key={c} className="flex items-start gap-2 text-sm text-slate-800" data-testid={`trial-cat-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {c}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2 text-xs text-slate-500">
          <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-400" /> 24 hours access</div>
          <div className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-slate-400" /> No card, ever</div>
        </div>

        {state === "starting" && (
          <p data-testid="start-trial-progress" className="mt-6 text-sm text-slate-500 text-center">Activating your pass…</p>
        )}

        {state === "error" && (
          <>
            <p data-testid="start-trial-error" className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              {err}
            </p>
            <button onClick={start} data-testid="start-trial-retry-btn"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#0B0F0D] hover:bg-[#1a2420] text-[#D4AF37] font-extrabold text-sm px-6 py-4 rounded-xl transition-colors">
              Try again <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}

        {state === "success" && (
          <button onClick={() => navigate("/app")} data-testid="start-trial-enter-btn"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0B0F0D] hover:bg-[#1a2420] text-[#D4AF37] font-extrabold text-sm px-6 py-4 rounded-xl transition-colors">
            Enter the dictionary <ArrowRight className="w-4 h-4" />
          </button>
        )}

        <p className="mt-4 text-[11px] text-slate-400 text-center leading-relaxed">
          One free pass per person. Full T&amp;Cs at <a href="/terms" className="underline">/terms</a>.
          After 24 hours your account returns to the free tier — nothing is charged.
        </p>
      </div>
    </div>
  );
}
